import { useEffect } from "react";
import DataStore from "../store/data.Store";

type UseAppWebSocketParams = {
  dataStore: DataStore;
  isCloudMode: boolean;
  targetMachineId: string;
  webClientId: string;
  operatorName: string;
  operatorId: string;
  defaultErrorTitle: string;
  errorCodeText: Record<string, string>;
  openMessageModal: (title?: string) => void;
};

type CloudEnvelope = {
  type?: unknown;
  data?: Record<string, any>;
};

function restoreIndexedPayload(payload: Record<string, any>) {
  const numericKeys = Object.keys(payload).filter((key) => /^\d+$/.test(key)).sort((a, b) => Number(a) - Number(b));
  if (!numericKeys.length) {
    return payload;
  }
  return numericKeys.map((key) => payload[key]);
}

function normalizeCloudAckPayload(data: Record<string, any>, action: string) {
  const nestedPayload = data?.data;

  if (nestedPayload !== undefined) {
    return nestedPayload;
  }

  const { machineId: _machineId, action: _action, ...rest } = data || {};

  // recipesAck 最终必须收敛成数组，保证页面按索引读取时稳定。
  if (action === "recipesAck" && rest && typeof rest === "object" && !Array.isArray(rest)) {
    return restoreIndexedPayload(rest);
  }

  return rest;
}

function safeParseMessage(raw: string): Record<string, any> | null {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object") {
      return parsed as Record<string, any>;
    }
  } catch (error) {
    // 异常消息直接忽略，保持和旧实现一样不因为单条脏数据中断页面。
  }
  return null;
}

function handleKioskModeMessage(
  parsed: Record<string, any>,
  dataStore: DataStore,
  defaultErrorTitle: string,
  errorCodeText: Record<string, string>,
  openMessageModal: (title?: string) => void,
) {
  if (parsed.action === "errorAck") {
    const nextTitle = errorCodeText[parsed?.data?.code] || defaultErrorTitle;
    openMessageModal(nextTitle);
    return;
  }
  dataStore.setData(parsed as any);
}

function handleCloudModeMessage(parsed: CloudEnvelope, dataStore: DataStore) {
  const type = String(parsed?.type || "").toUpperCase();

  if (type === "SYNC_STATE" || type === "SYNC_ERROR") {
    const data = parsed?.data || {};
    if (data?.action) {
      const action = String(data.action);
      const preferEnvelopeActions = new Set([
        "activeLogAck",
        "defaultAck",
        "loadSettingAck",
        "loadNavStatusAck",
        "recipesAck",
      ]);

      const payload = preferEnvelopeActions.has(action)
        ? normalizeCloudAckPayload(data, action)
        : (data.data ?? data);

      dataStore.setData({ action, data: payload } as any);
    }
    return;
  }

  if (type === "REGISTER_ACK" || type === "DEVICE_STATUS") {
    return;
  }

  if (type === "COMMAND_ACK") {
    const data = parsed?.data || {};
    if (data?.action) {
      dataStore.setData({ action: data.action, data } as any);
    }
  }
}

function buildSocketUrl(isCloudMode: boolean, webClientId: string) {
  if (isCloudMode) {
    const cloudWsBase = process.env.REACT_APP_CLOUD_WS_URL || ("ws://" + window.location.hostname + ":5002/ws-web");
    const wsUrl = new URL(cloudWsBase);
    wsUrl.searchParams.set("clientId", webClientId);
    return wsUrl.toString();
  }

  const localPort = process.env.REACT_APP_WS_PORT || "3000";
  return "ws://" + window.location.hostname + ":" + localPort + "/api/external/long-polling";
}

export function useAppWebSocket({
  dataStore,
  isCloudMode,
  targetMachineId,
  webClientId,
  operatorName,
  operatorId,
  defaultErrorTitle,
  errorCodeText,
  openMessageModal,
}: UseAppWebSocketParams) {
  useEffect(() => {
    // WebSocket 建连、消息适配和关闭清理由独立 hook 承担，App.tsx 只负责拿结果渲染。
    try {
      const ws = new WebSocket(buildSocketUrl(isCloudMode, webClientId));
      dataStore.setEws(ws);

      ws.onmessage = (evt) => {
        const parsed = safeParseMessage(evt.data);
        if (!parsed) {
          return;
        }

        if (!isCloudMode) {
          handleKioskModeMessage(parsed, dataStore, defaultErrorTitle, errorCodeText, openMessageModal);
          return;
        }

        handleCloudModeMessage(parsed, dataStore);
      };

      ws.onerror = () => { };

      ws.onopen = () => {
        if (!isCloudMode) {
          return;
        }

        const searchParams = new URLSearchParams(window.location.search);
        const finalOperatorName = String(
          operatorName
          || searchParams.get("operatorName")
          || searchParams.get("operator")
          || searchParams.get("username")
          || ""
        ).trim();
        const finalOperatorId = String(
          operatorId
          || searchParams.get("operatorId")
          || searchParams.get("userId")
          || ""
        ).trim();

        ws.send(JSON.stringify({
          type: "REGISTER",
          timestamp: Date.now(),
          operatorId: finalOperatorId,
          userId: finalOperatorId,
          operatorName: finalOperatorName,
          data: {
            role: "web",
            clientId: webClientId,
            machineId: targetMachineId,
            operator: finalOperatorName,
            operatorName: finalOperatorName,
            username: finalOperatorName,
            operatorId: finalOperatorId,
            userId: finalOperatorId,
          },
        }));
      };

      ws.onclose = () => {
        openMessageModal(defaultErrorTitle);
      };

      return () => {
        if (ws.readyState <= 1) {
          ws.close();
        }
        if (dataStore.ews === ws) {
          dataStore.setEws(null);
        }
      };
    } catch (error) {
      dataStore.setEws(null);
      return undefined;
    }
  }, [
    dataStore,
    defaultErrorTitle,
    errorCodeText,
    isCloudMode,
    openMessageModal,
    operatorId,
    operatorName,
    targetMachineId,
    webClientId,
  ]);
}
